import React, { useState, useEffect } from 'react';
import { MapContainer, TileLayer, Marker } from 'react-leaflet';
import { Modal } from 'react-bootstrap';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './Map.css';
import { getMapData } from '../../api';

/** API’den gelen olası konum adı alanları (ilk dolu olan kullanılır) */
const resolveItemLocationLabel = (item) => {
  if (!item || typeof item !== 'object') return null;
  return (
    item.city ||
    item.locationName ||
    item.placeName ||
    item.mapTitle ||
    item.mapLabel ||
    item.location ||
    item.sehir ||
    item.konum ||
    (typeof item.place === 'string' ? item.place : null) ||
    null
  );
};

const getLocationDisplayTitle = (loc) => {
  if (!loc) return 'Konum';
  if (loc.label) return loc.label;
  if (Array.isArray(loc.items)) {
    for (const it of loc.items) {
      const l = resolveItemLocationLabel(it);
      if (l) return l;
    }
  }
  const lat = Number.parseFloat(loc.lat);
  const lng = Number.parseFloat(loc.lng);
  if (!Number.isNaN(lat) && !Number.isNaN(lng)) {
    const near = nearestCityName(lat, lng);
    if (near) return near;
    return `Konum (${lat.toFixed(2)}, ${lng.toFixed(2)})`;
  }
  return 'Konum';
};

/** Büyük şehir merkezleri — API’de isim yoksa en yakın merkeze göre başlık */
const TURKEY_CITY_CENTERS = [
  [41.0082, 28.9784, 'İstanbul'],
  [38.4237, 27.1428, 'İzmir'],
  [39.9334, 32.8597, 'Ankara'],
  [40.1826, 29.0665, 'Bursa'],
  [36.8969, 30.7133, 'Antalya'],
  [37.0662, 37.3833, 'Gaziantep'],
  [37.0, 35.3213, 'Adana'],
  [37.8715, 32.4846, 'Konya'],
  [38.7312, 35.4787, 'Kayseri'],
  [36.8, 34.6333, 'Mersin'],
  [37.9144, 40.2306, 'Diyarbakır'],
  [39.7767, 30.5206, 'Eskişehir'],
  [41.0015, 39.7178, 'Trabzon'],
  [41.2928, 36.3313, 'Samsun'],
  [39.9043, 41.2678, 'Erzurum'],
  [38.4942, 43.38, 'Van'],
  [41.6771, 26.5556, 'Edirne'],
  [38.6191, 27.4289, 'Manisa'],
  [40.8533, 29.8815, 'Kocaeli'],
  [39.6484, 27.8826, 'Balıkesir'],
  [40.9833, 27.5167, 'Tekirdağ'],
  [40.1553, 26.4142, 'Çanakkale'],
  [37.2153, 28.3636, 'Muğla'],
  [41.4535, 27.3598, 'Kırklareli'],
  [37.1674, 38.7955, 'Şanlıurfa'],
  [37.7626, 27.4369, 'Aydın'],
  [37.1811, 33.215, 'Karaman'],
  [40.65, 35.8333, 'Çorum'],
  [40.5887, 43.7978, 'Kars'],
  [41.1105, 42.7022, 'Artvin'],
  [38.5012, 43.3729, 'Ağrı'],
  [41.7333, 27.4833, 'Lüleburgaz'],
  [40.9839, 37.8764, 'Ordu'],
  [40.4608, 39.4786, 'Gümüşhane'],
  [39.75, 39.5, 'Erzincan'],
  [38.6823, 29.4082, 'Uşak'],
  [40.9833, 37.8814, 'Tokat'],
  [39.8242, 34.8086, 'Yozgat'],
  [38.3704, 38.2658, 'Malatya'],
  [36.7184, 37.1212, 'Hatay'],
  [37.066, 27.267, 'Bodrum'],
];

function haversineKm(lat1, lon1, lat2, lon2) {
  const R = 6371;
  const toRad = (d) => (d * Math.PI) / 180;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) * Math.sin(dLon / 2);
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
}

/** Yakındaysa (km) şehir adı, değilse null — koordinat satırına düşer */
const MAX_CITY_MATCH_KM = 85;

function nearestCityName(lat, lng) {
  let best = null;
  let bestKm = Infinity;
  for (const [clat, clng, name] of TURKEY_CITY_CENTERS) {
    const km = haversineKm(lat, lng, clat, clng);
    if (km < bestKm) {
      bestKm = km;
      best = name;
    }
  }
  if (best && bestKm <= MAX_CITY_MATCH_KM) return best;
  return null;
}

// Leaflet icon fix for React
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

const Map = () => {
  const [mapData, setMapData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showModal, setShowModal] = useState(false);

  // Türkiye'nin merkez koordinatları
  const turkeyCenter = [39.0, 35.0];
  const defaultZoom = 6;

  useEffect(() => {
    const fetchMapData = async () => {
      try {
        setLoading(true);
        const response = await getMapData();
        console.log('Map API Response:', response);
        
        if (response.status && response.data) {
          // Koordinatlara göre gruplandır
          const groupedData = {};
          
          response.data.forEach(item => {
            const coordKey = `${item.lat},${item.lng}`;
            
            if (!groupedData[coordKey]) {
              groupedData[coordKey] = {
                lat: item.lat,
                lng: item.lng,
                label: resolveItemLocationLabel(item),
                items: []
              };
            } else {
              const nextLabel = resolveItemLocationLabel(item);
              if (nextLabel && !groupedData[coordKey].label) {
                groupedData[coordKey].label = nextLabel;
              }
            }
            
            groupedData[coordKey].items.push(item);
          });
          
          // Gruplanan veriyi array'e çevir
          const processedData = Object.values(groupedData);
          console.log('Grouped map data:', processedData);
          
          setMapData(processedData);
        } else {
          setError('Harita verileri yüklenemedi');
        }
      } catch (err) {
        console.error('Map data fetch error:', err);
        setError('Harita verileri yüklenirken hata oluştu');
      } finally {
        setLoading(false);
      }
    };

    fetchMapData();
  }, []);

  if (loading) {
    return (
      <div className="map-container">
        <div className="map-loading">
          <p>Harita yükleniyor...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="map-container">
        <div className="map-error">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="map-container">
 
      <div className="map-wrapper">
        <MapContainer
          center={turkeyCenter}
          zoom={defaultZoom}
          style={{ height: '600px', width: '100%' }}
          className="leaflet-map"
        >
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
          
          {mapData.map((location) => (
            <Marker
              key={`${location.lat}-${location.lng}`}
              position={[
                parseFloat(location.lat),
                parseFloat(location.lng)
              ]}
              eventHandlers={{
                click: () => {
                  setSelectedLocation(location);
                  setShowModal(true);
                }
              }}
            />
          ))}
        </MapContainer>
      </div>
      
      {mapData.length === 0 && (
        <div className="map-no-data">
          <p>Haritada gösterilecek veri bulunamadı</p>
        </div>
      )}

      {/* Custom Modal */}
      <Modal show={showModal} onHide={() => setShowModal(false)} size="lg" centered>
        <Modal.Header
          closeButton
          closeVariant="white"
          style={{ background: 'linear-gradient(135deg, #2c3e50 0%, #34495e 100%)', color: 'white', border: 'none' }}
        >
          <Modal.Title>
            {getLocationDisplayTitle(selectedLocation)}
            <div style={{fontSize: '0.9rem', opacity: 0.9, fontWeight: 400}}>
              {selectedLocation?.items.length} makale • {[...new Set(selectedLocation?.items.map(item => item.writerName) || [])].length} yazar
            </div>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{padding: '1.5rem', maxHeight: '400px', overflowY: 'auto'}}>
          {selectedLocation?.items.map((item, idx) => (
            <div key={item.id} 
                 className="modal-item-card" 
                 onClick={() => window.open(`/article/${item.id}`, '_blank')}
                 style={{
                   display: 'flex',
                   alignItems: 'center',
                   gap: '1rem',
                   padding: '1rem',
                   marginBottom: '0.5rem',
                   background: '#ffffff',
                   border: '1px solid #e9ecef',
                   borderRadius: '8px',
                   cursor: 'pointer',
                   transition: 'all 0.2s ease'
                 }}
                 onMouseEnter={(e) => {
                   e.target.style.background = '#f8f9fa';
                   e.target.style.borderColor = '#DC1A22';
                   e.target.style.transform = 'translateX(3px)';
                   e.target.style.boxShadow = '0 3px 10px rgba(220, 26, 34, 0.1)';
                 }}
                 onMouseLeave={(e) => {
                   e.target.style.background = '#ffffff';
                   e.target.style.borderColor = '#e9ecef';
                   e.target.style.transform = 'translateX(0)';
                   e.target.style.boxShadow = 'none';
                 }}>
              <div style={{
                background: '#DC1A22',
                color: 'white',
                width: '28px',
                height: '28px',
                borderRadius: '6px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '0.85rem',
                fontWeight: 600,
                flexShrink: 0
              }}>
                {idx + 1}
              </div>
              <div style={{flex: 1, minWidth: 0}}>
                <h6 style={{
                  fontSize: '0.95rem',
                  fontWeight: 600,
                  color: '#2c3e50',
                  margin: '0 0 0.3rem 0',
                  lineHeight: 1.3
                }}>
                  {item.title}
                </h6>
                <div style={{
                  fontSize: '0.85rem',
                  color: '#64748b',
                  fontStyle: 'italic',
                  fontWeight: 500
                }}>
                  {item.writerName}
                </div>
              </div>
              <div style={{
                color: '#DC1A22',
                opacity: 0.6,
                flexShrink: 0
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M8.59 16.59L13.17 12L8.59 7.41L10 6l6 6-6 6-1.41-1.41z"/>
                </svg>
              </div>
            </div>
          ))}
        </Modal.Body>
      </Modal>
    </div>
  );
};

export default Map;